import {ACCESS_TOKEN, COMPANY_ID, USER_ID} from '@constants/auth.constant'
import {LocalStorageService} from '@services/utils/localStorage.service'
import axios from 'axios'
import {ENV} from './ENV.config'

/**
 * The chat-service client (REACT_APP_GETWAY). Built by a factory so a second back end, if one is
 * ever needed, gets the same auth headers and the same 401/error handling.
 *
 * A core authentication rejection is HTTP 200 with {"status":401} in the body and no `code`, so
 * both shapes are treated as "signed out".
 */
const makeInstance = (baseURL: string) => {
  const instance = axios.create({
    baseURL,
    headers: {
      Accept: 'application/json',
    },
  })

  instance.interceptors.request.use(
    (config: any) => {
      const accessToken = LocalStorageService.get(ACCESS_TOKEN) || null
      const uid = LocalStorageService.get(USER_ID) || null
      const cid = LocalStorageService.get(COMPANY_ID) || null

      if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`
      if (uid) config.headers.uid = uid
      if (cid) config.headers.cid = cid

      return config
    },
    (error) => {
      if (error.response) {
        return Promise.reject({
          ...error.response.data,
          ...{code: error.response.data.code || error.status},
        })
      }

      return Promise.reject({
        body: false,
        code: 404,
        msg: 'Server not responding',
      })
    }
  )

  instance.interceptors.response.use(
    (res: any) => {
      if (res.data.code === 200) return {...res.data}

      if (res.data.code === 401 || res.data.status === 401) {
        LocalStorageService.clear()
        window.location.reload()
      }
      return Promise.reject({
        body: res.data.body,
        code: res.data.code,
        msg: res.data.msg,
      })
    },
    (error) => {
      if (error?.response) {
        if (error?.response?.data?.code === 401) {
          LocalStorageService.clear()
          window.location.reload()
        }

        // A refusal from a service's ACL or role gate is a real HTTP 403 whose sentence is in
        // `message` (core PermissionInterceptor, each service's AuthzInterceptor). Every caller toasts
        // `msg`, and axios 0.26 has no `error.status`, so without this a refusal reached the screen
        // with no code and an empty toast.
        if (error.response.status === 403) {
          const data = error.response.data || {}
          return Promise.reject({
            code: 403,
            msg: data.message || data.msg || 'You do not have permission to do this.',
            body: {},
          })
        }

        if (error?.response?.data?.error) {
          return Promise.reject({
            code: error?.response?.data?.code,
            msg: error?.response?.data?.error,
            body: {},
          })
        }

        return Promise.reject({
          ...error?.response?.data,
          ...{code: error.response.data.code || error.status},
        })
      } else {
        return Promise.reject({
          code: 500,
          msg: 'Server not responding',
          body: {},
        })
      }
    }
  )

  return instance
}

export const axiosIns = makeInstance(ENV.apiEndpoint)
